"use client";

import { TextShimmer } from "@/components/text-shimmer";
import { Button } from "@/components/ui/button";
import { encodedHolidayParams } from "@/feature/holiday/search/lib/holiday-formatter-helper";
import {
  createInitialHolidaySearchData,
  getLastHolidaySearch,
  saveLastHolidaySearch,
} from "@/feature/holiday/search/params/holiday-search-persistence";
import { cn } from "@/lib/utils";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch } from "@/redux/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useTransition,
} from "react";
import { useForm } from "react-hook-form";
import {
  holidaySearchSchema,
  type IHolidaySearchSchema,
} from "../../schema/holiday-search.schema";
import { IHolidaySearchQueryParams } from "../../types/holiday-search-type";
import { HolidayFields } from "./holiday-fields";

interface IProps {
  search?: IHolidaySearchQueryParams;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  open?: boolean;
  hiddenWidget?: boolean;
}

export default function HolidaySearchForm({
  search,
  hiddenWidget,
  setOpen,
}: IProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const defaultValues = useMemo(() => {
    return createInitialHolidaySearchData(search);
  }, [search]);

  const form = useForm<IHolidaySearchSchema>({
    resolver: zodResolver(holidaySearchSchema),
    defaultValues,
  });

  useEffect(() => {
    if (search) {
      return;
    }

    const storedSearch = getLastHolidaySearch();
    if (!storedSearch) {
      return;
    }

    form.reset(storedSearch);
  }, [form, search]);

  const dispatch = useAppDispatch();

  const onSubmit = useCallback(
    (data: IHolidaySearchSchema) => {
      saveLastHolidaySearch(data);

      startTransition(() => {
        const params = encodedHolidayParams({
          city_id: data.city?.city_id ? String(data.city.city_id) : undefined,
          city: data.city?.city,
          country: data.city?.country_name,
        });
        dispatch(baseApi.util.invalidateTags(["HOTEL_SEARCH"]));
        router.push(params ? `/holiday?${params}` : "/holiday", {
          scroll: false,
        });
        setOpen?.(false);
      });
    },
    [router, setOpen, dispatch],
  );

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="h-full w-full">
      {!hiddenWidget && (
        <div className="mb-5 flex flex-wrap items-center gap-2 md:gap-4">
          <HolidayFields.SearchType />
        </div>
      )}

      <div
        className={cn(
          "grid items-start gap-3",
          "grid-cols-1 md:grid-cols-[minmax(0,1fr)_52px] xl:grid-cols-[minmax(0,1fr)_56px]",
        )}
      >
        <HolidayFields.Location form={form} />

        <Button
          type="submit"
          className="h-12 rounded-md bg-primary px-4 text-primary-foreground hover:bg-primary/90 md:size-13 md:min-w-13 md:px-0 xl:size-14 xl:min-w-14"
          size="lg"
          disabled={isPending}
          aria-label="Search holiday"
        >
          <Search className="h-5 w-5" />
          <span className="md:hidden">
            {isPending ? (
              <TextShimmer duration={1.2}>Search</TextShimmer>
            ) : (
              "Search"
            )}
          </span>
        </Button>
      </div>
    </form>
  );
}
